import React, { useState, useEffect } from 'react';
import { Share2, Plus, CheckCircle, Clock, Target, TrendingUp, X, Twitter, Linkedin, Instagram, Facebook, Youtube, MessageCircle, MessageSquare, Calendar, Edit } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';
import { dailyTaskService } from '@/services/dailyTaskService';
import { toast } from 'sonner';

interface SocialActivity {
  id: string;
  user_id: string;
  activity_type: string;
  content: string;
  status: string;
  due_date: string;
  created_at: string;
  updated_at: string;
  metadata?: any;
  // Helper properties for UI
  platform?: 'twitter' | 'linkedin' | 'instagram' | 'facebook' | 'tiktok' | 'youtube';
  description?: string;
  completed?: boolean;
}

const platformConfig = {
  twitter: { 
    name: 'Twitter/X', 
    icon: Twitter, 
    color: 'bg-black', 
    textColor: 'text-black',
    activities: [
      'Share a product update thread',
      'Engage with 5 potential customers',
      'Post about your startup journey',
      'Share industry insights',
      'Retweet relevant content with commentary'
    ]
  },
  linkedin: { 
    name: 'LinkedIn', 
    icon: Linkedin, 
    color: 'bg-blue-600', 
    textColor: 'text-blue-600',
    activities: [
      'Post about your business milestone',
      'Share a professional insight',
      'Connect with 10 industry professionals',
      'Comment on relevant posts',
      'Share your startup story'
    ]
  },
  instagram: { 
    name: 'Instagram', 
    icon: Instagram, 
    color: 'bg-gradient-to-r from-purple-500 to-pink-500', 
    textColor: 'text-purple-600',
    activities: [
      'Post behind-the-scenes content',
      'Share product photos/videos',
      'Create Stories about your process',
      'Engage with followers comments',
      'Post motivational content'
    ]
  },
  facebook: { 
    name: 'Facebook', 
    icon: Facebook, 
    color: 'bg-blue-500', 
    textColor: 'text-blue-500',
    activities: [
      'Share in relevant business groups',
      'Post company updates',
      'Engage with community posts',
      'Share customer testimonials',
      'Create event for product launch'
    ]
  },
  youtube: { 
    name: 'YouTube', 
    icon: Youtube, 
    color: 'bg-red-500', 
    textColor: 'text-red-500',
    activities: [
      'Upload product demo video',
      'Create tutorial content',
      'Share startup journey vlog',
      'Comment on industry videos',
      'Create shorts about your product'
    ]
  },
  tiktok: { 
    name: 'TikTok', 
    icon: MessageCircle, 
    color: 'bg-black', 
    textColor: 'text-black',
    activities: [
      'Create product showcase video',
      'Share quick tips/hacks',
      'Behind-the-scenes content',
      'Trending audio with your twist',
      'Day in the life of founder'
    ]
  }
};

const SocialTracker: React.FC = () => {
  const { user } = useAuth();
  const [activities, setActivities] = useState<SocialActivity[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedPlatform, setSelectedPlatform] = useState<keyof typeof platformConfig>('twitter');
  const [showCustomForm, setShowCustomForm] = useState(false);
  const [customActivity, setCustomActivity] = useState('');
  const [showAddForm, setShowAddForm] = useState(false);

  const platformIcons: Record<string, React.ComponentType<any>> = {
    twitter: Twitter,
    linkedin: Linkedin,
    youtube: Youtube,
    instagram: Instagram,
    facebook: Facebook,
    tiktok: MessageSquare,
    reddit: MessageSquare,
    medium: Edit
  };

  const platformColors: Record<string, string> = {
    twitter: 'bg-blue-500',
    linkedin: 'bg-blue-700',
    youtube: 'bg-red-600',
    instagram: 'bg-pink-500',
    facebook: 'bg-blue-600',
    tiktok: 'bg-black',
    reddit: 'bg-orange-500',
    medium: 'bg-green-600'
  };

  useEffect(() => {
    if (user?.id) {
      loadActivities();
      
      // Create default activities if none exist
      const timer = setTimeout(() => {
        if (activities.length === 0) {
          initializeActivitiesIfNeeded();
        }
      }, 1000);
      
      return () => clearTimeout(timer);
    }
  }, [user?.id]);

  const loadActivities = async () => {
    if (!user?.id) return;

    try {
      const { data, error } = await (supabase as any)
        .from('social_activities')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (error) throw error;
      
      // Transform data to match component expectations
      const transformedData = (data || []).map((item: any) => ({
        ...item,
        platform: item.metadata?.platform || 'twitter',
        description: item.content,
        completed: item.status === 'completed'
      }));
      
      setActivities(transformedData as SocialActivity[]);
    } catch (error) {
      console.error('Error loading social activities:', error);
    } finally {
      setLoading(false);
    }
  };

  const initializeActivitiesIfNeeded = async () => {
    if (!user?.id) return;
    try {
      await createDefaultActivities();
    } catch (error) {
      console.error('Error initializing activities:', error);
    }
  };

  const createDefaultActivities = async () => {
    if (!user?.id) return;

    try {
      // Check if user already has activities
      const { data: existingActivities } = await (supabase as any)
        .from('social_activities')
        .select('id')
        .eq('user_id', user.id)
        .limit(1);

      if (existingActivities && existingActivities.length > 0) {
        return; // User already has activities
      }

      // Create default activities from platform config
      const defaultActivities: any[] = [];
      Object.entries(platformConfig).forEach(([platform, config]) => {
        config.activities.forEach((activity, index) => {
          defaultActivities.push({
            user_id: user.id,
            activity_type: 'post',
            content: activity,
            status: 'pending',
            due_date: new Date(Date.now() + (index + 1) * 24 * 60 * 60 * 1000).toISOString(),
            metadata: { platform }
          });
        });
      });

      const { error } = await (supabase as any)
        .from('social_activities')
        .insert(defaultActivities);

      if (error) {
        console.error('Error creating default activities:', error);
      } else {
        loadActivities();
      }
    } catch (error) {
      console.error('Error creating default activities:', error);
    }
  };

  const toggleActivity = async (activityId: string, completed: boolean) => {
    try {
      const { error } = await (supabase as any)
        .from('social_activities')
        .update({
          status: !completed ? 'completed' : 'pending',
          updated_at: new Date().toISOString()
        })
        .eq('id', activityId);

      if (error) throw error;

      setActivities(prev => prev.map(activity => 
        activity.id === activityId 
          ? { 
              ...activity,
              status: !completed ? 'completed' : 'pending',
              completed: !completed
            }
          : activity
      ));

      // Mark daily task as completed if activity is completed
      if (!completed && user?.id) {
        await dailyTaskService.markDailyTaskCompleted(user.id, 'social_media');
      }

      // Show success popup
      const confirmDiv = document.createElement('div');
      confirmDiv.className = 'fixed top-4 right-4 bg-green-500 text-white px-6 py-3 rounded-lg shadow-lg z-50 animate-fade-in';
      confirmDiv.textContent = !completed ? '✅ Activity completed!' : '↩️ Activity unmarked';
      document.body.appendChild(confirmDiv);
      setTimeout(() => {
        if (document.body.contains(confirmDiv)) {
          document.body.removeChild(confirmDiv);
        }
      }, 3000);

    } catch (error) {
      console.error('Error updating activity:', error);
    }
  };

  const addCustomActivity = async (description?: string) => {
    const activityDescription = description || customActivity.trim();
    if (!user?.id || !activityDescription) return;

    try {
      const { data, error } = await (supabase as any)
        .from('social_activities')
        .insert({
          user_id: user.id,
          activity_type: 'post',
          content: activityDescription,
          status: description ? 'completed' : 'pending',
          due_date: new Date().toISOString(),
          metadata: { platform: selectedPlatform }
        })
        .select()
        .single();

      if (error) throw error;

      // Transform the response to match component expectations
      const transformedData = {
        ...data,
        platform: data.metadata?.platform || selectedPlatform,
        description: data.content,
        completed: data.status === 'completed'
      };

      setActivities(prev => [...prev, transformedData as SocialActivity]);
      setCustomActivity('');
      
      // Show success message
      if (description) {
        toast.success(`Activity logged for ${platformConfig[selectedPlatform].name}!`);
      }

    } catch (error) {
      console.error('Error adding custom activity:', error);
      if (description) {
        toast.error('Failed to log activity. Please try again.');
      }
    }
  };

  const addPredefinedActivity = async (platform: keyof typeof platformConfig, description: string) => {
    if (!user?.id) return;

    try {
      const { data, error } = await (supabase as any)
        .from('social_activities')
        .insert({
          user_id: user.id,
          activity_type: 'post',
          content: description,
          status: 'pending',
          due_date: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString(),
          metadata: { platform }
        })
        .select()
        .single();

      if (error) throw error;

      // Transform the response to match component expectations
      const transformedData = {
        ...data,
        platform: data.metadata?.platform || platform,
        description: data.content,
        completed: data.status === 'completed'
      };

      setActivities(prev => [...prev, transformedData as SocialActivity]);
    } catch (error) {
      console.error('Error adding predefined activity:', error);
    }
  };

  const todayStr = new Date().toISOString().slice(0, 10);
  const todayActivities = activities.filter(activity => {
    if (!activity.due_date) return false;
    const activityDateStr = new Date(activity.due_date).toISOString().split('T')[0];
    return activityDateStr === todayStr;
  });

  const completedToday = todayActivities.filter(activity => activity.completed || activity.status === 'completed').length;
  const totalToday = todayActivities.length;
  const completionPercentage = totalToday > 0 ? (completedToday / totalToday) * 100 : 0;

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-startup-teal mx-auto mb-4"></div>
          <p className="text-startup-gray">Loading your social activities...</p>
        </div>
      </div>
    );
  }
  return (
    <div className="p-6 space-y-6">
      {/* Today's Progress */}
      <div className="card-startup animate-fade-in bg-gradient-to-r from-startup-teal/10 to-startup-gold/10 border-2 border-startup-teal/20">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold text-startup-navy">Today's Progress</h2>
          <div className="text-right">
            <div className="text-3xl font-bold text-startup-teal">{Math.round(completionPercentage)}%</div>
            <div className="text-startup-gray text-sm">Complete</div>
          </div>
        </div>
        <div className="mb-4">
          <div className="bg-startup-gray/20 rounded-full h-3 overflow-hidden">
            <div 
              className="bg-gradient-startup h-full rounded-full transition-all duration-1000 ease-out"
              style={{ width: `${completionPercentage}%` }}
            />
          </div>
        </div>
        <p className="text-startup-gray">
          {completedToday} of {totalToday} activities completed
        </p>
      </div>

      {/* Quick Actions */}
      <div className="card-startup animate-fade-in">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-startup-navy">Quick Actions</h3>
        </div>

        <div className="bg-startup-slate rounded-xl p-4 animate-fade-in">
          <div className="flex items-center gap-4">
            {/* Platform Selector */}
            <select
              value={selectedPlatform}
              onChange={(e) => setSelectedPlatform(e.target.value as keyof typeof platformConfig)}
              className="flex-1 px-3 py-2 border border-startup-gray/20 rounded-lg focus:outline-none focus:ring-2 focus:ring-startup-teal"
            >
              {Object.entries(platformConfig).map(([key, config]) => (
                <option key={key} value={key}>{config.name}</option>
              ))}
            </select>

            {/* Quick Complete Button */}
            <button
              onClick={() => {
                const description = `Posted on ${platformConfig[selectedPlatform].name} - ${new Date().toLocaleDateString()}`;
                addCustomActivity(description);
              }}
              className="btn-startup-primary flex items-center space-x-2 whitespace-nowrap"
            >
              <span>✓</span>
              <span>Mark as Done</span>
            </button>
          </div>

          {/* Optional Description */}
          <div className="mt-4">
            <input
              type="text"
              value={customActivity}
              onChange={(e) => setCustomActivity(e.target.value)}
              placeholder="Optional: Add details about your post..."
              className="w-full px-3 py-2 border border-startup-gray/20 rounded-lg focus:outline-none focus:ring-2 focus:ring-startup-teal"
              onKeyDown={(e) => e.key === 'Enter' && addCustomActivity()}
            />
          </div>
        </div>
      </div>

      {/* Platform Checklists */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {Object.entries(platformConfig).map(([platform, config]) => {
          const PlatformIcon = config.icon as React.ComponentType<any>;
          const platformActivities = activities.filter(a => 
            (a.platform || a.metadata?.platform) === platform
          );
          const completedCount = platformActivities.filter(a => 
            a.completed || a.status === 'completed'
          ).length;

          return (
            <div key={platform} className="card-startup animate-fade-in">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center space-x-3">
                  <div className={`p-2 rounded-lg ${config.color} text-white`}>
                    <PlatformIcon className="w-5 h-5" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-startup-navy">{config.name}</h3>
                    <p className="text-sm text-startup-gray">
                      {completedCount}/{platformActivities.length} completed
                    </p>
                  </div>
                </div>
              </div>

              {/* Suggested quick-adds */}
              <div className="flex flex-wrap gap-2 mb-4">
                {config.activities.map((label: string) => (
                  <button
                    key={label}
                    onClick={() => addPredefinedActivity(platform as keyof typeof platformConfig, label)}
                    className="px-2 py-1 text-xs bg-startup-slate rounded hover:bg-startup-slate/80"
                  >
                    + {label}
                  </button>
                ))}
              </div>

              <ul className="space-y-2">
                {platformActivities.map((act) => (
                  <li key={act.id} className="flex items-center justify-between p-3 bg-startup-slate rounded">
                    <div className="flex items-center space-x-3">
                      <input
                        type="checkbox"
                        checked={act.completed || act.status === 'completed'}
                        onChange={() => toggleActivity(act.id, act.completed || act.status === 'completed')}
                        className="h-4 w-4"
                      />
                      <span className={(act.completed || act.status === 'completed') ? 'line-through text-startup-gray' : ''}>{act.description || act.content}</span>
                    </div>
                    <span className="text-xs text-startup-gray">Due: {new Date(act.due_date).toLocaleDateString()}</span>
                  </li>
                ))}
                {platformActivities.length === 0 && (
                  <li className="text-startup-gray text-sm">No activities yet. Use the suggestions above to add one.</li>
                )}
              </ul>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default SocialTracker;
